import {
  IPersonalReservationInfo,
  IReservationStorageData,
  IReservationStorageGuests,
  IReservationStorageInitParams,
  IReservationStorageSelection,
} from 'static/interfaces'

/*
 * 予約導線で使う `sessionStorage` の唯一の窓口。
 *
 * ## 保存するキー
 *
 * - `reservationStorageData`: 画面遷移をまたいで予約対象・日程・参加人数・予約作成結果を渡す状態。
 * - `personalReservationInfo`: 支払い画面で入力中のゲスト情報。入力途中で画面を開き直した場合の復元に使う。
 * - `reservationCompletedTransactionId`: 同一タブで予約完了イベントを二重送信しないための予約番号。
 *
 * `reservationStorageData` の各プロパティは導線の段階によって欠け得る。画面固有の必須項目は
 * 呼び出し元で確認すること（例: 支払い画面の `memberPost`）。このモジュールは上記3キー以外を
 * 走査・変更しない。上記3キーのトップレベルにあるスキーマ外のプロパティは、読み書き時に削除する。
 */

/**
 *  * ## `reservationStorageData` のライフサイクル
 *
 * 1. ツアー詳細・空き確認で `initParams`（日付・参加人数）を保存して日付選択画面へ遷移する。
 * 2. 日付・開始時刻の確定時に `guests`、`memberPost`、`reservation` を追加して支払い画面へ遷移する。
 *    `memberPost` は支払い画面に進むための必須状態であり、存在しない場合はツアー詳細へ戻す。
 * 3. 支払い成功後に `chargePrice`、`hasCoupon`、`createdReservation` を追加して完了画面へ遷移する。
 * 4. 完了画面を開いた時点、明細画面から戻る時、またはサインアウト時に予約関連の状態を削除する。
 */
const RESERVATION_STORAGE_DATA_KEY = 'reservationStorageData'
const PERSONAL_RESERVATION_INFO_KEY = 'personalReservationInfo'
const RESERVATION_COMPLETED_TRANSACTION_ID_KEY = 'reservationCompletedTransactionId'
const PERSONAL_RESERVATION_INFO_KEYS = [
  'other_requests',
  'pickup_location',
  'pickup_latitude',
  'pickup_longitude',
  'country_code',
  'email',
  'fullname',
  'tel',
]

/**
 * `reservationStorageData` の現在のスキーマバージョン。
 *
 * v1より前はバージョンを持たないため、読み込み時に内容を保ったままv1として再保存する。
 * v2を導入する際は、この値と `IReservationStorageData.version` を更新し、
 * `migrateReservationStorageData` にv1からv2への変換を追加する。
 */
export const RESERVATION_STORAGE_DATA_VERSION = 1

type ReservationStorageDataV1 = IReservationStorageData & { version: 1 }

const isPlainObject = (value: any): value is { [key: string]: any } =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const hasOnlyKeys = (value: { [key: string]: any }, allowedKeys: string[]) =>
  Object.keys(value).every(key => allowedKeys.includes(key))

const isGuestCount = (value: any) =>
  typeof value === 'number' && Number.isFinite(value) && Number.isInteger(value) && value >= 0

const isReservationStorageGuests = (value: any): value is IReservationStorageGuests =>
  isPlainObject(value) &&
  hasOnlyKeys(value, ['adults', 'children', 'infants']) &&
  isGuestCount(value.adults) &&
  isGuestCount(value.children) &&
  isGuestCount(value.infants)

const isReservationStorageInitParams = (value: any): value is IReservationStorageInitParams =>
  isPlainObject(value) &&
  hasOnlyKeys(value, ['dates', 'guests']) &&
  (value.dates === undefined ||
    (Array.isArray(value.dates) && value.dates.every(date => typeof date === 'string'))) &&
  (value.guests === undefined || isReservationStorageGuests(value.guests))

const isReservationStorageSelection = (value: any): value is IReservationStorageSelection =>
  isPlainObject(value) &&
  hasOnlyKeys(value, ['selectedDate', 'totalPrice', 'departure']) &&
  (value.selectedDate === undefined || typeof value.selectedDate === 'string') &&
  (value.totalPrice === undefined ||
    value.totalPrice === null ||
    (typeof value.totalPrice === 'number' && Number.isFinite(value.totalPrice))) &&
  (value.departure === undefined || value.departure === null || isPlainObject(value.departure))

// createdReservationはサーバーからの返却値を保持しているため検証から除外している
const isReservationStorageDataV1 = (value: any): value is ReservationStorageDataV1 =>
  isPlainObject(value) &&
  value.version === RESERVATION_STORAGE_DATA_VERSION &&
  (value.initParams === undefined || isReservationStorageInitParams(value.initParams)) &&
  (value.guests === undefined || isReservationStorageGuests(value.guests)) &&
  (value.memberPost === undefined || isPlainObject(value.memberPost)) &&
  (value.reservation === undefined || isReservationStorageSelection(value.reservation)) &&
  (value.chargePrice === undefined ||
    (typeof value.chargePrice === 'number' && Number.isFinite(value.chargePrice))) &&
  (value.hasCoupon === undefined || typeof value.hasCoupon === 'boolean')

const normalizeReservationStorageData = (value: any): ReservationStorageDataV1 => ({
  version: RESERVATION_STORAGE_DATA_VERSION,
  ...(value.initParams === undefined ? {} : { initParams: value.initParams }),
  ...(value.guests === undefined ? {} : { guests: value.guests }),
  ...(value.memberPost === undefined ? {} : { memberPost: value.memberPost }),
  ...(value.reservation === undefined ? {} : { reservation: value.reservation }),
  ...(value.chargePrice === undefined ? {} : { chargePrice: value.chargePrice }),
  ...(value.hasCoupon === undefined ? {} : { hasCoupon: value.hasCoupon }),
  ...(value.createdReservation === undefined
    ? {}
    : { createdReservation: value.createdReservation }),
})

const normalizePersonalReservationInfo = (value: any): Partial<IPersonalReservationInfo> => {
  if (!isPlainObject(value)) {
    return {}
  }

  const data = PERSONAL_RESERVATION_INFO_KEYS.reduce((result, key) => {
    if (value[key] !== undefined) {
      result[key] = value[key]
    }
    return result
  }, {} as { [key: string]: any })
  if (value.pickup_location_selection_error !== undefined) {
    data.pickup_location_selection_error = value.pickup_location_selection_error
  }
  return data
}

const parseJson = (value: string | null): any | null => {
  if (!value) {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (_) {
    return null
  }
}

const reportStorageWriteFailure = (key: string, error: any) => {
  // 個人入力値を含めず、失敗した管理対象キーと例外だけを記録する。
  console.error(`[reservationSessionStorage] Failed to write: ${key}`, error)
}

/**
 * 現行バージョンへ変換する入口。v2以降を追加する場合は、`version: 1` の値を
 * `version: 2` へ変換する処理をここに追加し、定数を更新する。
 */
const migrateReservationStorageData = (value: any): ReservationStorageDataV1 | null => {
  if (!isPlainObject(value)) {
    return null
  }

  // バージョン導入前の保存値は、内容を変えずにv1として扱う。
  const version = value.version === undefined ? RESERVATION_STORAGE_DATA_VERSION : value.version
  if (version !== RESERVATION_STORAGE_DATA_VERSION) {
    return null
  }

  const migratedValue = normalizeReservationStorageData(value)
  return isReservationStorageDataV1(migratedValue) ? migratedValue : null
}

const writeReservationStorageData = (data: IReservationStorageData): boolean => {
  try {
    const serializedData = JSON.stringify(normalizeReservationStorageData(data))
    const parsedData = parseJson(serializedData)

    if (!isReservationStorageDataV1(parsedData)) {
      reportStorageWriteFailure(
        RESERVATION_STORAGE_DATA_KEY,
        new Error('Invalid reservation storage data')
      )
      return false
    }

    sessionStorage.setItem(RESERVATION_STORAGE_DATA_KEY, serializedData)
    return true
  } catch (error) {
    reportStorageWriteFailure(RESERVATION_STORAGE_DATA_KEY, error)
    return false
  }
}

/**
 * ストレージにある予約状態を内部用に取得する。
 *
 * バージョン導入前の値へ `version: 1` を補完し、スキーマ外のトップレベルプロパティを削除する。
 */
const readReservationStorageData = (): ReservationStorageDataV1 | null => {
  const rawData = sessionStorage.getItem(RESERVATION_STORAGE_DATA_KEY)
  if (!rawData) {
    return null
  }

  const data = migrateReservationStorageData(parseJson(rawData))
  if (!data) {
    sessionStorage.removeItem(RESERVATION_STORAGE_DATA_KEY)
    return null
  }

  if (rawData !== JSON.stringify(data)) {
    writeReservationStorageData(data)
  }

  return data
}

/**
 * 予約導線の状態を取得する。壊れたJSON・未知のバージョン・不正な値は削除し、
 * 空の状態として扱う。バージョン情報のない既存データは取得時にv1へ移行する。
 */
export const getReservationStorageData = (): IReservationStorageData => {
  const data = readReservationStorageData()
  return data || {}
}

/**
 * `reservationStorageData` をv1の内容で置き換える。
 *
 * ツアー詳細から予約導線を開始・再開始する箇所で使う。以前のツアー・日程・予約結果を
 * 引き継がないため、部分更新ではなくこの関数を使う。JSON化された実際の保存値を検証し、
 * 不正な値は保存せず `false` を返す。この場合、既存の正常な保存値は維持する。
 */
export const setReservationStorageData = (
  data: Omit<IReservationStorageData, 'version'>
): boolean => {
  return writeReservationStorageData({
    ...data,
    version: RESERVATION_STORAGE_DATA_VERSION,
  })
}

/**
 * 現在の `reservationStorageData` を保持したまま、指定したトップレベルの項目を追加・更新する。
 *
 * 日付選択から支払い画面へ進む時や、支払い成功後に予約結果を追加する時に使う。
 * ネストしたオブジェクトはマージしないため、`initParams` を更新する場合は既存の
 * `initParams` を展開してから渡す。保存できた場合は `true`、保存をスキップした場合は
 * `false` を返す。
 */
export const updateReservationStorageData = (
  data: Omit<Partial<IReservationStorageData>, 'version'>
): boolean => {
  const currentData = getReservationStorageData()
  return setReservationStorageData({ ...currentData, ...data })
}

/**
 * `reservationStorageData` を削除する。
 *
 * 支払い画面へ必要な状態がない場合、予約完了画面の初期化時、予約明細からツアー詳細へ戻る時、
 * およびサインアウト時に呼ぶ。
 * 個人入力情報も消す必要があるフローでは `clearReservationStorageAndInfo` を使う。
 */
export const clearReservationStorageData = () => {
  sessionStorage.removeItem(RESERVATION_STORAGE_DATA_KEY)
}

const isPersonalReservationInfo = (value: any): value is IPersonalReservationInfo =>
  isPlainObject(value) &&
  hasOnlyKeys(value, [...PERSONAL_RESERVATION_INFO_KEYS, 'pickup_location_selection_error']) &&
  PERSONAL_RESERVATION_INFO_KEYS.every(key => typeof value[key] === 'string') &&
  (value.pickup_location_selection_error === undefined ||
    typeof value.pickup_location_selection_error === 'boolean')

const writePersonalReservationInfo = (data: Partial<IPersonalReservationInfo>): boolean => {
  if (!isPersonalReservationInfo(data)) {
    reportStorageWriteFailure(
      PERSONAL_RESERVATION_INFO_KEY,
      new Error('Invalid personal reservation info')
    )
    return false
  }

  try {
    sessionStorage.setItem(PERSONAL_RESERVATION_INFO_KEY, JSON.stringify(data))
    return true
  } catch (error) {
    reportStorageWriteFailure(PERSONAL_RESERVATION_INFO_KEY, error)
    return false
  }
}

/**
 * 支払いフォームの途中入力を取得する。
 *
 * 未入力・初回表示では空オブジェクトを返すため、呼び出し側では各項目を任意として扱う。
 * 保存済みデータは必須キーが揃っていることを確認し、満たさない場合は削除して `{}` を返す。
 * 送迎場所の警告状態は任意項目とし、追加前の保存データも読み込めるようにする。
 * フォームはログイン中のゲスト情報を初期値として使う。
 * 不正なJSONまたはスキーマ不正の値は削除する。
 */
export const getPersonalReservationInfo = (): Partial<IPersonalReservationInfo> => {
  const rawData = sessionStorage.getItem(PERSONAL_RESERVATION_INFO_KEY)
  if (!rawData) {
    return {}
  }

  const data = normalizePersonalReservationInfo(parseJson(rawData))
  if (!isPersonalReservationInfo(data)) {
    sessionStorage.removeItem(PERSONAL_RESERVATION_INFO_KEY)
    return {}
  }
  if (rawData !== JSON.stringify(data)) {
    writePersonalReservationInfo(data)
  }
  return data
}

/**
 * 支払いフォームの途中入力を検証して保存する。
 *
 * 保存対象は氏名・メールアドレス・電話番号・国コード・要望・送迎場所
 * 予約作成リクエスト自体の値ではなく、フォーム更新ごとに全項目を渡す。
 * 保存できた場合は `true`、保存をスキップした場合は `false` を返す。
 */
export const setPersonalReservationInfo = (data: IPersonalReservationInfo): boolean =>
  writePersonalReservationInfo(normalizePersonalReservationInfo(data))

/** 支払い画面を離れて予約導線を終了する時に、個人入力の途中保存を削除する。 */
const clearPersonalReservationInfo = () => {
  sessionStorage.removeItem(PERSONAL_RESERVATION_INFO_KEY)
}

export const clearReservationStorageAndInfo = () => {
  clearReservationStorageData()
  clearPersonalReservationInfo()
}

/**
 * 同一タブで最後に完了計測を送った予約番号を取得する。
 *
 * 予約完了画面は再表示され得るため、`createdReservation.no` と比較して同じ場合は
 * `reservation_completed` のdataLayerイベントを送らない。
 */
export const getReservationCompletedTransactionId = (): string | null => {
  const transactionId = sessionStorage.getItem(RESERVATION_COMPLETED_TRANSACTION_ID_KEY)
  if (transactionId === null || transactionId.length > 0) {
    return transactionId
  }

  sessionStorage.removeItem(RESERVATION_COMPLETED_TRANSACTION_ID_KEY)
  return null
}

/** `reservation_completed` を送信した直後に、その予約番号を検証して保存する。 */
export const setReservationCompletedTransactionId = (transactionId: string): boolean => {
  if (typeof transactionId !== 'string' || transactionId.length === 0) {
    reportStorageWriteFailure(
      RESERVATION_COMPLETED_TRANSACTION_ID_KEY,
      new Error('Invalid reservation completed transaction id')
    )
    return false
  }

  try {
    sessionStorage.setItem(RESERVATION_COMPLETED_TRANSACTION_ID_KEY, transactionId)
    return true
  } catch (error) {
    reportStorageWriteFailure(RESERVATION_COMPLETED_TRANSACTION_ID_KEY, error)
    return false
  }
}
